import { useEffect, useState, type FC, type FormEvent } from "react";
import { PageOptions } from "../../types/page";
import { useTranslation } from "../../contexts/useTranslation";
import Navbar from "../../components/Navbar";
import ArrowLeftIcon from "../../components/icons/ArrowLeftIcon";
import ArrowRightIcon from "../../components/icons/ArrowRightIcon";
import { Interweave } from "interweave";
import storageService from "../../core/services/storageService";
import { useCore } from "../../contexts/useCore";
import { useWidget } from "../../contexts/useWidget";
import { Slot, Templated } from "../../templating";

interface EnterCodePageProps {
  navigate: (pageOptions: PageOptions) => void;
  params: Record<string, string>;
  isFullscreen: boolean;
}

const EnterCodePage: FC<EnterCodePageProps> = ({ navigate, params, isFullscreen }) => {
  const { translate } = useTranslation();
  const core = useCore();
  const { storageScope } = useWidget();
  const [submitEnabled, setSubmitEnabled] = useState(false);
  const [loginEmail] = useState<string>(params.email);
  const [loginCode, setLoginCode] = useState("");
  const [error, setError] = useState<string | null>(null);

  const handleFormSubmit = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();
    try {
      // core.submitLoginCode() handles storageService.setContact + state.contact +
      // auth:succeeded emission internally.
      await core.submitLoginCode(loginCode, loginEmail);
      navigate({ page: storageService.getPageBeforeLogin(storageScope) });
    } catch (err) {
      const status = (err as { cause?: { status?: number } })?.cause?.status;
      if (status === 422) {
        setError(translate("code_is_invalid_or_has_already_expired"));
        setLoginCode("");
      } else if (status !== 499) {
        navigate({ page: { type: "errorPage" } });
      }
    }
  };

  useEffect(() => {
    const loginCodeRegex = /^[0-9]{6}$/;
    setSubmitEnabled(loginCodeRegex.test(loginCode.trim()));
  }, [loginCode]);

  return (
    <Templated id="lastbot-enter-code-page">
      <>
        <Slot name="navbar">
          <Navbar isFullscreen={isFullscreen} hideLogin>
            <button
              type="button"
              onClick={() => navigate({ page: { type: "login" } })}
              className="-ml-1 text-lastbot-icon-muted cursor-pointer"
            >
              <ArrowLeftIcon />
            </button>
          </Navbar>
        </Slot>
        <div className="flex-1 flex flex-col gap-y-3 px-4 text-lastbot-on-surface justify-center items-center text-center max-w-[490px] w-full self-center">
          <div className="text-3xl tracking-wide text-lastbot-on-surface">
            <Slot name="form-title">{translate("enter_one_time_code")}</Slot>
          </div>
          <Slot name="form-description">
            <Interweave
              content={translate("we_just_sent_a_one_time_login_code", { email: `<strong>${loginEmail}</strong>` })}
              containerTagName="div"
              className="text-xs/5 text-lastbot-icon-muted sm:px-2"
            />
          </Slot>
          <div className="w-full mt-2 sm:px-4">
            <form
              onSubmit={handleFormSubmit}
              className="bg-lastbot-surface-muted rounded-full relative"
            >
              <Slot name="code-input">
                <input
                  type="text"
                  value={loginCode}
                  onChange={(event) => setLoginCode(event.target.value)}
                  placeholder={"XXXXXX"}
                  className="block w-full bg-transparent text-center text-lastbot-on-surface resize-none h-[54px] py-3 px-14 placeholder:text-lastbot-on-surface-subtle focus:outline-hidden"
                  autoFocus
                  data-testid={`login-code-input`}
                />
              </Slot>
              <button
                type="submit"
                disabled={!submitEnabled}
                className="absolute bottom-0 right-[7px] bg-lastbot-send-surface text-lastbot-on-send-surface rounded-full p-2 mb-[7.5px] cursor-pointer disabled:text-lastbot-disabled-on-surface disabled:cursor-not-allowed disabled:bg-lastbot-disabled-surface"
                data-testid={`login-code-submit`}
              >
                <Slot name="submit-button"><ArrowRightIcon /></Slot>
              </button>
            </form>
            {error && (
              <Slot name="form-error">
                <div className="px-6 pt-3 text-xs text-center text-lastbot-danger-strong" data-testid={`login-code-error`}>{error}</div>
              </Slot>
            )}
          </div>
        </div>
      </>
    </Templated>
  );
};

export default EnterCodePage;
